import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
} from "recharts";
import {
  Users, ListChecks, Music4, Euro, Sparkles, RefreshCw, ArrowLeft, LogOut, Brain,
  Activity, Shield, Eye, Wand2, Bell, X, Globe,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { LANGS } from "../lib/translations";
import { Logo } from "../components/Logo";

const COLORS = ["#FF5500", "#6366F1", "#10B981", "#F59E0B", "#EC4899", "#06B6D4"];

export default function Admin() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [txs, setTxs] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [creds, setCreds] = useState({ current_password: "", new_username: "", new_password: "" });
  const [credsBusy, setCredsBusy] = useState(false);
  const [notif, setNotif] = useState({ unseen_count: 0, items: [] });
  const [notifOpen, setNotifOpen] = useState(false);
  const prevUnseen = useRef(null);
  const [tab, setTab] = useState("users");

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { state: { from: "/admin" } }); return; }
    if (user.role !== "admin") { toast.error(t("admin.adminRequired")); navigate("/"); return; }
    loadAll();
    fetchNotifications();
    const iv = setInterval(fetchNotifications, 25000);
    return () => clearInterval(iv);
  }, [user, loading]); // eslint-disable-line

  const loadAll = async () => {
    try {
      const [s, u, w, t, a] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/users"),
        api.get("/admin/waitlist"), api.get("/admin/transactions"),
        api.get("/admin/analytics"),
      ]);
      setStats(s.data); setUsers(u.data.users); setWaitlist(w.data.waitlist); setTxs(t.data.transactions);
      setAnalytics(a.data);
    } catch (e) { toast.error(t("admin.loadError")); }
  };

  const submitCreds = async (e) => {
    e.preventDefault();
    if (!creds.current_password) { toast.error(t("admin.enterCurrent")); return; }
    if (!creds.new_username && !creds.new_password) { toast.error(t("admin.enterNew")); return; }
    setCredsBusy(true);
    try {
      const payload = { current_password: creds.current_password };
      if (creds.new_username) payload.new_username = creds.new_username;
      if (creds.new_password) payload.new_password = creds.new_password;
      await api.put("/admin/credentials", payload);
      toast.success(t("admin.credsUpdated"));
      setCreds({ current_password: "", new_username: "", new_password: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setCredsBusy(false); }
  };

  const fetchNotifications = async () => {
    try {
      const { data } = await api.get("/admin/notifications");
      if (prevUnseen.current !== null && data.unseen_count > prevUnseen.current) {
        const latest = data.items.find((i) => i.is_new);
        toast.success(`${t("admin.newPaymentReceived")}${latest ? ` — ${latest.plan} · ${latest.amount}€` : ""} 🎉`);
      }
      prevUnseen.current = data.unseen_count;
      setNotif(data);
    } catch (e) {}
  };

  const openNotif = async () => {
    const willOpen = !notifOpen;
    setNotifOpen(willOpen);
    if (willOpen && notif.unseen_count > 0) {
      try { await api.post("/admin/notifications/seen"); } catch (e) {}
      prevUnseen.current = 0;
      setNotif((n) => ({ ...n, unseen_count: 0, items: n.items.map((i) => ({ ...i, is_new: false })) }));
    }
  };

  const runAnalysis = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/ai-analysis");
      setAnalysis(data);
      toast.success(t("admin.analysisGenerated"));
    } catch (e) { toast.error(t("admin.analysisUnavailable")); }
    finally { setAiBusy(false); }
  };

  if (!user || user.role !== "admin") return null;
  const locale = lang === "fr" ? "fr-FR" : lang === "es" ? "es-ES" : "en-US";

  const revenueData = analysis?.summary?.revenue_by_plan || [];
  const qualityData = analysis?.summary?.mixes_by_quality || [];
  const mostUsed = (analytics?.most_used || []).map((x) => {
    const k = `admin.ev.${x.type}`;
    const v = t(k);
    return { ...x, label: v === k ? x.label : v };
  });
  const daily = analytics?.daily || [];

  const metrics = [
    { icon: Users, tid: "utilisateurs", label: t("admin.mUsers"), value: stats?.total_users ?? "—", color: "#6366F1" },
    { icon: ListChecks, tid: "waitlist", label: t("admin.mWaitlist"), value: stats?.total_waitlist ?? "—", color: "#06B6D4" },
    { icon: Music4, tid: "mixages", label: t("admin.mMixes"), value: stats?.total_mixes ?? "—", color: "#10B981" },
    { icon: Euro, tid: "revenu", label: t("admin.mRevenue"), value: stats ? `${stats.revenue}€` : "—", color: "#FF5500" },
  ];

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F8FAFC]">
      <header className="sticky top-0 z-20 bg-[#0B0D12]/95 backdrop-blur border-b border-[#262C3A] px-4 sm:px-6 py-3 flex items-center gap-3">
        <button onClick={() => navigate("/")} className="text-[#64748B] hover:text-white transition-colors"><ArrowLeft className="h-5 w-5" /></button>
        <Logo className="h-8 w-8" withText textClass="text-lg text-white" />
        <span className="font-mono text-xs uppercase tracking-widest text-[#64748B] hidden sm:inline">{t("admin.consoleTag")}</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-0.5 mr-1 rounded-full border border-[#262C3A] p-0.5" data-testid="admin-lang-switcher">
            <Globe className="h-3.5 w-3.5 text-[#64748B] mx-1" />
            {LANGS.map((l) => (
              <button key={l.code} onClick={() => setLang(l.code)} data-testid={`admin-lang-${l.code}`}
                className={`text-xs font-semibold rounded-full px-2 py-1 transition-colors ${lang === l.code ? "bg-[#FF5500] text-white" : "text-[#94A3B8] hover:bg-[#1A1F2C]"}`}>
                {l.flag}
              </button>
            ))}
          </div>
          <div className="relative">
            <button onClick={openNotif} data-testid="admin-notif-bell"
              className="relative inline-flex items-center gap-2 bg-[#1A1F2C] hover:bg-[#262C3A] rounded-xl px-3 py-2 text-sm transition-colors">
              <Bell className="h-4 w-4" />
              {notif.unseen_count > 0 && (
                <span data-testid="admin-notif-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center">
                  {notif.unseen_count}
                </span>
              )}
            </button>
            {notifOpen && (
              <div data-testid="admin-notif-panel" className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-[#131722] border border-[#262C3A] rounded-2xl shadow-2xl z-40 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#262C3A]">
                  <span className="font-heading font-bold text-sm flex items-center gap-2"><Euro className="h-4 w-4 text-[#10B981]" /> {t("admin.recentPayments")}</span>
                  <button onClick={() => setNotifOpen(false)} className="text-[#64748B] hover:text-white transition-colors"><X className="h-4 w-4" /></button>
                </div>
                <div className="max-h-80 overflow-auto tx-dark-scroll">
                  {notif.items.length ? notif.items.map((it) => (
                    <div key={it.id} className={`px-4 py-3 border-b border-[#1A1F2C] flex items-center justify-between gap-2 ${it.is_new ? "bg-[#10B981]/5" : ""}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{it.plan || t("admin.payment")}</div>
                        <div className="text-[11px] text-[#64748B]">{fmtDate(it.at, locale)}</div>
                      </div>
                      <span className="font-mono text-sm text-[#10B981] shrink-0">{it.amount}€</span>
                    </div>
                  )) : <p className="px-4 py-8 text-center text-xs text-[#64748B]">{t("admin.noPayments")}</p>}
                </div>
              </div>
            )}
          </div>
          <button onClick={loadAll} className="inline-flex items-center gap-2 bg-[#1A1F2C] hover:bg-[#262C3A] rounded-xl px-3 py-2 text-sm transition-colors"><RefreshCw className="h-4 w-4" /> {t("admin.refresh")}</button>
          <button onClick={() => { logout(); navigate("/"); }} className="inline-flex items-center gap-2 text-[#64748B] hover:text-white px-2 transition-colors"><LogOut className="h-4 w-4" /></button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
        {/* METRICS */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {metrics.map((m, i) => (
            <motion.div key={m.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-[#131722] border border-[#262C3A] rounded-2xl p-5" data-testid={`metric-${m.tid}`}>
              <m.icon className="h-6 w-6 mb-3" style={{ color: m.color }} />
              <div className="font-heading text-3xl font-extrabold">{m.value}</div>
              <div className="text-sm text-[#64748B] mt-1">{m.label}</div>
            </motion.div>
          ))}
        </div>

        {/* TRAFFIC / USAGE */}
        <div className="bg-[#131722] border border-[#262C3A] rounded-2xl p-5 sm:p-6" data-testid="admin-traffic">
          <div className="flex items-center gap-2 mb-4"><Activity className="h-5 w-5 text-[#06B6D4]" /><h2 className="font-heading text-xl font-bold">{t("admin.trafficTitle")}</h2></div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            {[
              { label: t("admin.events"), value: analytics?.total_events ?? "—", icon: Activity, color: "#06B6D4" },
              { label: t("admin.pageviews"), value: analytics?.pageviews ?? "—", icon: Eye, color: "#6366F1" },
              { label: t("admin.activeUsers"), value: analytics?.unique_users ?? "—", icon: Users, color: "#10B981" },
              { label: t("admin.aiTracks"), value: analytics?.total_generated_tracks ?? "—", icon: Wand2, color: "#8B5CF6" },
            ].map((m) => (
              <div key={m.label} className="bg-[#0B0D12] rounded-2xl p-4">
                <m.icon className="h-5 w-5 mb-2" style={{ color: m.color }} />
                <div className="font-heading text-2xl font-extrabold">{m.value}</div>
                <div className="text-xs text-[#64748B] mt-1">{m.label}</div>
              </div>
            ))}
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-[#0B0D12] rounded-2xl p-4">
              <p className="font-mono text-xs uppercase text-[#64748B] mb-2">{t("admin.mostUsed")}</p>
              {mostUsed.length ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={mostUsed} layout="vertical" margin={{ left: 8, right: 12 }}>
                    <XAxis type="number" tick={{ fill: "#64748B", fontSize: 10 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="label" width={140} tick={{ fill: "#94A3B8", fontSize: 10 }} />
                    <Tooltip formatter={(v, n, p) => [`${v} (${p.payload.percent}%)`, t("admin.eventsUnit")]} contentStyle={{ background: "#131722", border: "1px solid #262C3A", borderRadius: 12 }} />
                    <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                      {mostUsed.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-xs text-[#64748B] py-12 text-center">{t("admin.noActivity")}</p>}
            </div>
            <div className="bg-[#0B0D12] rounded-2xl p-4">
              <p className="font-mono text-xs uppercase text-[#64748B] mb-2">{t("admin.traffic14")}</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={daily}>
                  <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 9 }} interval={1} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 10 }} allowDecimals={false} />
                  <Tooltip formatter={(v) => [v, t("admin.eventsUnit")]} contentStyle={{ background: "#131722", border: "1px solid #262C3A", borderRadius: 12 }} />
                  <Bar dataKey="count" fill="#06B6D4" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* AI ANALYSIS */}
        <div className="bg-gradient-to-br from-[#131722] to-[#1A1F2C] border border-[#262C3A] rounded-2xl p-5 sm:p-6">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div className="flex items-center gap-2"><Brain className="h-5 w-5 text-[#8B5CF6]" /><h2 className="font-heading text-xl font-bold">{t("admin.aiAnalysisTitle")}</h2></div>
            <button onClick={runAnalysis} disabled={aiBusy} data-testid="run-ai-analysis-button"
              className="inline-flex items-center gap-2 bg-[#8B5CF6] hover:bg-[#7c3aed] disabled:opacity-50 rounded-xl px-4 py-2 font-semibold transition-colors">
              <Sparkles className="h-4 w-4" /> {aiBusy ? t("admin.analyzing") : t("admin.runAnalysis")}
            </button>
          </div>

          {!analysis && <p className="text-[#64748B] text-sm">{t("admin.analysisHint")}</p>}

          {analysis && (
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-[#0B0D12] rounded-2xl p-4">
                  <p className="font-mono text-xs uppercase text-[#64748B] mb-2">{t("admin.revenueByPlan")}</p>
                  {revenueData.length ? (
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie data={revenueData} dataKey="percent" nameKey="label" innerRadius={40} outerRadius={70} paddingAngle={3}>
                          {revenueData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v, n, p) => [`${v}%`, p.payload.label]} contentStyle={{ background: "#131722", border: "1px solid #262C3A", borderRadius: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="text-xs text-[#64748B] py-10 text-center">{t("admin.noPaymentYet")}</p>}
                  <div className="space-y-1 mt-2">
                    {revenueData.map((r, i) => (
                      <div key={r.label} className="flex items-center justify-between text-xs">
                        <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{r.label}</span>
                        <span className="font-mono">{r.percent}% · {r.value}€</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-[#0B0D12] rounded-2xl p-4">
                  <p className="font-mono text-xs uppercase text-[#64748B] mb-2">{t("admin.mixesByQuality")}</p>
                  {qualityData.length ? (
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={qualityData}>
                        <XAxis dataKey="label" tick={{ fill: "#64748B", fontSize: 10 }} />
                        <YAxis tick={{ fill: "#64748B", fontSize: 10 }} />
                        <Tooltip formatter={(v, n, p) => [`${p.payload.percent}% (${v})`, t("admin.mixesUnit")]} contentStyle={{ background: "#131722", border: "1px solid #262C3A", borderRadius: 12 }} />
                        <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                          {qualityData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-xs text-[#64748B] py-10 text-center">{t("admin.noMixYet")}</p>}
                  <div className="flex justify-between text-xs mt-2 font-mono text-[#94A3B8]">
                    <span>{t("admin.conversion")}: {analysis.summary.conversion_rate_percent}%</span>
                    <span>{t("admin.paid")}: {analysis.summary.paid_transactions}/{analysis.summary.total_transactions}</span>
                  </div>
                </div>
              </div>
              <div className="bg-[#0B0D12] rounded-2xl p-4">
                <p className="font-mono text-xs uppercase text-[#64748B] mb-2">{t("admin.aiInsights")}</p>
                <pre className="text-sm text-[#cbd5e1] whitespace-pre-wrap font-sans leading-relaxed" data-testid="ai-insights">{analysis.insights}</pre>
              </div>
            </div>
          )}
        </div>

        {/* TABLES */}
        <div>
          <div className="flex gap-2 mb-4">
            {[["users", t("admin.tabUsers")], ["waitlist", t("admin.tabWaitlist")], ["tx", t("admin.tabTx")]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === k ? "bg-[#FF5500] text-white" : "bg-[#131722] text-[#94A3B8] hover:bg-[#1A1F2C]"}`}>{l}</button>
            ))}
          </div>
          <div className="bg-[#131722] border border-[#262C3A] rounded-2xl overflow-hidden">
            {tab === "users" && <Table empty={t("admin.noData")} cols={[t("admin.colName"), t("admin.colEmail"), t("admin.colRole"), t("admin.colPlan"), t("admin.colJoined")]} rows={users.map((u) => [u.name, u.email, u.role, u.plan, fmtDate(u.created_at, locale)])} />}
            {tab === "waitlist" && <Table empty={t("admin.noData")} cols={[t("admin.colEmail"), t("admin.colName"), t("admin.colInterest"), t("admin.colDate")]} rows={waitlist.map((w) => [w.email, w.name || "—", w.interest || "—", fmtDate(w.created_at, locale)])} />}
            {tab === "tx" && <Table empty={t("admin.noData")} cols={[t("admin.colPlan"), t("admin.colAmount"), t("admin.colStatus"), t("admin.colSession"), t("admin.colDate")]} rows={txs.map((t) => [t.plan, `${t.amount}${(t.currency || "eur").toUpperCase() === "EUR" ? "€" : t.currency}`, t.payment_status, (t.session_id || "").slice(0, 14) + "…", fmtDate(t.created_at, locale)])} />}
          </div>
        </div>

        {/* SECURITY / CREDENTIALS */}
        <div className="bg-[#131722] border border-[#262C3A] rounded-2xl p-5 sm:p-6" data-testid="admin-security">
          <div className="flex items-center gap-2 mb-2"><Shield className="h-5 w-5 text-[#10B981]" /><h2 className="font-heading text-xl font-bold">{t("admin.securityTitle")}</h2></div>
          <p className="text-sm text-[#64748B] mb-4">{t("admin.securityDesc")}</p>
          <form onSubmit={submitCreds} className="grid sm:grid-cols-3 gap-3" data-testid="admin-creds-form">
            <input type="password" required autoComplete="current-password" placeholder={t("admin.currentPassword")}
              value={creds.current_password} onChange={(e) => setCreds((c) => ({ ...c, current_password: e.target.value }))}
              data-testid="creds-current" className="bg-[#0B0D12] border border-[#262C3A] rounded-xl px-4 py-3 outline-none focus:border-[#10B981] transition-colors" />
            <input type="text" placeholder={t("admin.newUsername")}
              value={creds.new_username} onChange={(e) => setCreds((c) => ({ ...c, new_username: e.target.value }))}
              data-testid="creds-username" className="bg-[#0B0D12] border border-[#262C3A] rounded-xl px-4 py-3 outline-none focus:border-[#10B981] transition-colors" />
            <input type="password" autoComplete="new-password" placeholder={t("admin.newPassword")}
              value={creds.new_password} onChange={(e) => setCreds((c) => ({ ...c, new_password: e.target.value }))}
              data-testid="creds-password" className="bg-[#0B0D12] border border-[#262C3A] rounded-xl px-4 py-3 outline-none focus:border-[#10B981] transition-colors" />
            <button disabled={credsBusy} data-testid="creds-submit"
              className="sm:col-span-3 inline-flex items-center justify-center gap-2 bg-[#10B981] hover:bg-[#0ea371] disabled:opacity-50 rounded-xl py-3 font-semibold text-white transition-colors">
              <Shield className="h-4 w-4" /> {credsBusy ? t("admin.updating") : t("admin.updateCreds")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function fmtDate(d, locale = "fr-FR") { try { return new Date(d).toLocaleDateString(locale); } catch { return "—"; } }

function Table({ cols, rows, empty = "—" }) {
  return (
    <div className="overflow-auto tx-dark-scroll">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[#64748B] font-mono text-xs uppercase border-b border-[#262C3A]">
            {cols.map((c) => <th key={c} className="px-4 py-3 font-medium">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[#1A1F2C] hover:bg-[#1A1F2C] transition-colors">
              {r.map((cell, j) => <td key={j} className="px-4 py-3 whitespace-nowrap">{cell}</td>)}
            </tr>
          ))}
          {!rows.length && <tr><td className="px-4 py-8 text-center text-[#64748B]" colSpan={cols.length}>{empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
